// The channel registry rules (SPEC F6 "channel registry"), in one place for
// `npm run channels:check`, the crate build and the in-app import, so they
// can't drift. Pure: the valid style ids are passed in (the scripts read
// styles.json, the app uses STYLE_IDS; a test keeps the two equal).

export type Era = 'reissue' | 'new' | 'mixed';
export const ERAS: readonly Era[] = ['reissue', 'new', 'mixed'];

/** SPEC F6 genres: lowercase, trimmed, spaces to hyphens; duplicates are merged by normalizeGenres. */
export const normalizeGenre = (g: string): string => g.trim().toLowerCase().replace(/\s+/g, '-');

export const normalizeGenres = (genres: readonly string[]): string[] => [...new Set(genres.map(normalizeGenre).filter(Boolean))];

export interface RegistryEntry {
  id?: string;
  handle?: string;
  name: string;
  styles: string[];
  genres: string[];
  weight: 1 | 2 | 3 | 4;
  era: Era;
  enabled: boolean;
}

export interface RegistryIssue {
  /** 0-based position in the channels list; -1 for the file as a whole */
  index: number;
  /** 1-based line in the file, when the raw text was given */
  line?: number;
  field: string;
  message: string;
  fix: string;
  /** The entry's name, when it has one, to make the message findable */
  name?: string;
}

export interface RegistryResult {
  channels: RegistryEntry[];
  errors: RegistryIssue[];
  warnings: RegistryIssue[];
}

export const CHANNEL_ID_RE = /^UC[A-Za-z0-9_-]{22}$/;
export const HANDLE_RE = /^@\S+$/;
/** ROADMAP v0.3: the crate wants 30+ channels. */
export const MIN_ENABLED = 30;

/** Per-entry checks. Returns the clean entry, or the issues that stop it. */
export function validateChannel(raw: unknown, index: number, styleIds: readonly string[]): { entry?: RegistryEntry; issues: RegistryIssue[] } {
  const issues: RegistryIssue[] = [];
  const add = (field: string, message: string, fix: string, name?: string) => issues.push({ index, field, message, fix, name });

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    add('(entry)', 'not an object', 'Write each channel as { "id": …, "name": …, "styles": [...], "genres": [...], "weight": 1-4, "enabled": true }.');
    return { issues };
  }
  const c = raw as Record<string, unknown>;
  const name = typeof c.name === 'string' ? c.name.trim() : '';
  const label = name || undefined;

  if (!name) add('name', 'needs a name', 'Give the channel a name, e.g. "name": "Soul Jazz Uploads".');

  const id = typeof c.id === 'string' ? c.id.trim() : '';
  const handle = typeof c.handle === 'string' ? c.handle.trim() : '';
  if (c.id !== undefined && typeof c.id !== 'string') add('id', 'id must be text', 'Write the id in quotes, e.g. "id": "UC…".', label);
  if (c.handle !== undefined && typeof c.handle !== 'string') add('handle', 'handle must be text', 'Write the handle in quotes, e.g. "handle": "@name".', label);
  if (!id && !handle) {
    add('id', 'needs an id or a handle', 'Set "id" to the channel ID (UC plus 22 characters), or leave it empty and set "handle" to "@name".', label);
  }
  if (id && !CHANNEL_ID_RE.test(id)) {
    add('id', `"${id}" is not a channel ID`, 'A channel ID is UC followed by 22 letters, digits, - or _ (24 characters). Copy it from the channel\'s page, or use "handle" instead.', label);
  }
  if (handle && !HANDLE_RE.test(handle)) {
    add('handle', `"${handle}" is not a handle`, 'A handle starts with @ and has no spaces, e.g. "@somechannel".', label);
  }

  const styles = c.styles;
  if (!Array.isArray(styles) || styles.length === 0) {
    add('styles', 'needs at least one style', `List style ids, e.g. "styles": ["peterock"]. Style ids: ${styleIds.join(', ')}.`, label);
  } else {
    for (const s of styles) {
      if (typeof s !== 'string' || !styleIds.includes(s)) {
        add('styles', `"${String(s)}" is not one of the style ids`, `Use one of: ${styleIds.join(', ')}.`, label);
      }
    }
  }

  if (!Array.isArray(c.genres) || !c.genres.every((g) => typeof g === 'string')) {
    add('genres', 'genres must be a list of text', 'Write genres as a list, e.g. "genres": ["soul", "70s soul"] (it can be empty).', label);
  }
  if (c.weight !== 1 && c.weight !== 2 && c.weight !== 3 && c.weight !== 4) {
    add('weight', 'weight must be 1, 2, 3 or 4', 'Set "weight" to 1, 2, 3 or 4 (a number, no quotes).', label);
  }
  if (c.era !== undefined && !ERAS.includes(c.era as Era)) {
    add('era', 'era must be reissue, new or mixed', 'Set "era" to "reissue", "new" or "mixed", or leave it out for mixed.', label);
  }
  if (typeof c.enabled !== 'boolean') {
    add('enabled', 'enabled must be true or false', 'Set "enabled" to true or false (no quotes).', label);
  }

  if (issues.length > 0) return { issues };
  return {
    issues,
    entry: {
      id: id || undefined,
      handle: handle || undefined,
      name,
      styles: styles as string[],
      genres: normalizeGenres(c.genres as string[]),
      weight: c.weight as 1 | 2 | 3 | 4,
      era: (c.era as Era | undefined) ?? 'mixed',
      enabled: c.enabled as boolean,
    },
  };
}

/**
 * The whole registry: `{ channels: [...] }` (a bare list is accepted too, for
 * the app's import). Entries with errors are left out of `channels`, never
 * guessed at. Duplicates are checked among the valid entries only.
 */
export function validateRegistry(input: unknown, opts: { styleIds: readonly string[]; text?: string; allowBareList?: boolean }): RegistryResult {
  const errors: RegistryIssue[] = [];
  const warnings: RegistryIssue[] = [];
  const list = Array.isArray(input) && opts.allowBareList ? input : (input as { channels?: unknown } | null)?.channels;
  if (!input || typeof input !== 'object' || !Array.isArray(list)) {
    errors.push({ index: -1, line: 1, field: 'channels', message: 'no "channels" list found', fix: 'The file must look like { "channels": [ { … }, … ] }.' });
    return { channels: [], errors, warnings };
  }

  const lines = opts.text ? entryLines(opts.text) : undefined;
  const lineFor = (index: number, field: string) => (lines ? fieldLine(opts.text ?? '', lines, index, field) : undefined);

  const channels: RegistryEntry[] = [];
  const seenId = new Map<string, number>();
  const seenHandle = new Map<string, number>();
  list.forEach((raw, i) => {
    const { entry, issues } = validateChannel(raw, i, opts.styleIds);
    for (const issue of issues) errors.push({ ...issue, line: lineFor(i, issue.field) });
    if (!entry) return;

    let duplicate = false;
    const idKey = entry.id?.toLowerCase();
    const handleKey = entry.handle?.toLowerCase();
    if (idKey !== undefined) {
      const first = seenId.get(idKey);
      if (first !== undefined) {
        duplicate = true;
        errors.push({ index: i, line: lineFor(i, 'id'), field: 'id', name: entry.name, message: `the id "${entry.id}" is already used by entry ${first + 1}`, fix: 'Keep one entry per channel: remove or merge the duplicate.' });
      } else seenId.set(idKey, i);
    }
    if (handleKey !== undefined) {
      const first = seenHandle.get(handleKey);
      if (first !== undefined) {
        duplicate = true;
        errors.push({ index: i, line: lineFor(i, 'handle'), field: 'handle', name: entry.name, message: `the handle "${entry.handle}" is already used by entry ${first + 1} (handles ignore case)`, fix: 'Keep one entry per channel: remove or merge the duplicate.' });
      } else seenHandle.set(handleKey, i);
    }
    if (!duplicate) channels.push(entry);
  });

  const enabled = channels.filter((c) => c.enabled);
  const uncovered = opts.styleIds.filter((s) => !enabled.some((c) => c.styles.includes(s)));
  if (uncovered.length > 0) {
    warnings.push({
      index: -1,
      field: 'styles',
      message: `no enabled channel is tagged ${uncovered.length === opts.styleIds.length ? 'with any style' : uncovered.join(', ')}`,
      fix: 'Dig has nothing to pick for a style until a channel tagged with it is enabled.',
    });
  }
  if (enabled.length < MIN_ENABLED) {
    warnings.push({ index: -1, field: 'enabled', message: `${enabled.length} enabled ${enabled.length === 1 ? 'channel' : 'channels'}; the crate wants at least ${MIN_ENABLED}`, fix: 'Add and enable more channels (ROADMAP v0.3).' });
  }
  return { channels, errors, warnings };
}

/** "channels.json:14 (entry 3 "Name", styles): message. Fix: …" */
export function formatIssue(issue: RegistryIssue, file: string): string {
  const where = issue.line ? `${file}:${issue.line}` : file;
  const entry = issue.index >= 0 ? `entry ${issue.index + 1}${issue.name ? ` "${issue.name}"` : ''}, ` : '';
  return `${where} (${entry}${issue.field}): ${issue.message}. Fix: ${issue.fix}`;
}

// --- Line numbers -----------------------------------------------------------
// A small scanner over the raw JSON text that records where each element of
// the channels list starts and ends. It only needs to follow strings and
// brackets; the text has already been parsed, so it is valid JSON.

function entryLines(text: string): { start: number; end: number }[] {
  const out: { start: number; end: number }[] = [];
  let depth = 0;
  let inString = false;
  let listDepth = -1;
  let current = -1;
  let lastKey = '';
  let keyStart = -1;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') i++;
      else if (ch === '"') {
        inString = false;
        if (keyStart >= 0) lastKey = text.slice(keyStart + 1, i);
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      keyStart = i;
      if (depth === listDepth && current < 0) current = i;
      continue;
    }
    if (ch === '{' || ch === '[') {
      if (depth === listDepth && current < 0) current = i;
      depth++;
      // The channels list: the "channels" value at the top level, or a bare top-level list
      if (ch === '[' && listDepth < 0 && ((depth === 2 && lastKey === 'channels') || depth === 1)) listDepth = depth;
      continue;
    }
    if (ch === '}' || ch === ']') {
      depth--;
      if (depth === listDepth && current >= 0) {
        out.push({ start: current, end: i });
        current = -1;
      }
      if (ch === ']' && depth === listDepth - 1) listDepth = -2; // done
      continue;
    }
    if (depth === listDepth) {
      if (ch === ',') {
        if (current >= 0) out.push({ start: current, end: i - 1 });
        current = -1;
      } else if (!/\s/.test(ch ?? '') && current < 0) current = i;
    }
  }
  return out;
}

function fieldLine(text: string, spans: { start: number; end: number }[], index: number, field: string): number | undefined {
  const span = spans[index];
  if (!span) return undefined;
  const at = text.slice(span.start, span.end + 1).indexOf(`"${field}"`);
  const offset = at >= 0 ? span.start + at : span.start;
  return text.slice(0, offset).split('\n').length;
}
